import { Injectable, inject } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SessionService } from '../services/session.service';
import { AuthService } from '../services/auth/auth.service';
import { Router } from '@angular/router';
import { ToastService } from 'src/utils/toast.service';
import { BaseModalService } from 'src/utils/base-modal.service';
import { ModalService } from 'src/utils/modal.service';
import { InputModalService } from 'src/utils/input-modal.service';
import { OptionsModalService } from 'src/utils/options-modal.service';
import { environment } from '@environments/environment';

// El backend usa 403 para dos cosas distintas: el estado de la cuenta (usuario o
// compañía inactiva, sin compañía asignada) y la denegación de un permiso del rol.
// El primero debe cerrar la sesión; el segundo no, porque la sesión sigue siendo
// válida y lo que corresponde es explicarle al usuario qué le falta.
//
// Solo los 403 de autorización traen `code`, así que se listan aquí. Los de estado
// de cuenta viajan como texto sin código y siguen cerrando sesión.
//
// Esta lista hay que ampliarla cada vez que el backend agregue un código de
// autorización nuevo. La regla se podrá invertir —cerrar sesión solo con los
// códigos de estado de cuenta— cuando TODO 403 traiga `code`.
const AUTHORIZATION_ERROR_CODES = [
  // Permiso del rol denegado por el PermissionsGuard de Workflow.
  'CUSTOM_ACTION_NOT_ALLOWED',
  'INSUFFICIENT_PERMISSIONS',
  // Alcance de la Bandeja Equipo. La vista resuelve estos dos por su cuenta
  // —oculta el selector de equipo o cae al equipo por defecto—, y no puede
  // hacerlo si la sesión se cierra antes de que el error le llegue.
  'TEAM_INBOX_NOT_SUPERVISOR',
  'TEAM_INBOX_TEAM_NOT_SUPERVISED',
  'CASE_FILE_OUT_OF_SCOPE',
  'WORKFLOW_START_TEAM_NOT_ALLOWED',
];

// ms-products (PW-4209/PW-10) no tiene el concepto de `code` en su ErrorResult:
// cualquier 403 suyo llega sin código, igual que un 403 de estado de cuenta.
// No podemos distinguirlo por forma del body, así que se exime por origen de
// la petición: un rol sin acceso al módulo de Productos recibe 403 al pedir
// datos de soporte (p.ej. el catálogo de productos de Mis Procesos) y debe
// quedarse en su sesión, no ser deslogueado.
function isProductApiRequest(req: HttpRequest<any>): boolean {
  return req.url.startsWith(environment.productAPI);
}

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private session = inject(SessionService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private baseModalService = inject(BaseModalService);
  private modalService = inject(ModalService);
  private inputModalService = inject(InputModalService);
  private optionsModalService = inject(OptionsModalService);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const isProductPermissionDenial = error.status === 403 && isProductApiRequest(req);

        if (error.status === 401 || (error.status === 403 && !this.isAuthorizationDenial(error) && !isProductPermissionDenial)) {
          this.authService.sessionExpired = true;
          this.session.clearSession();

          this.baseModalService.forceClose();
          this.modalService.forceClose();
          this.inputModalService.hide();
          this.optionsModalService.hide();

          const reason = error.headers?.get('X-Session-Expired-Reason') || error.error?.expiredReason || '';
          this.authService.forceLogoutWithReason(reason);

          return throwError(() => error);
        }

        if (isProductPermissionDenial) {
          this.toastService.showToast(
            'fi fi-sr-exclamation text-[24px] text-warning',
            'No tienes permisos para acceder a esta sección.',
            'warning'
          );
        }

        return throwError(() => error);
      })
    );
  }

  private isAuthorizationDenial(error: HttpErrorResponse): boolean {
    const code = (error.error as { code?: string } | null)?.code;
    return !!code && AUTHORIZATION_ERROR_CODES.includes(code);
  }
}
